import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { getAuthHeader } from "../utils/auth";
import { MENU_GROUPS, getAllPermissionPaths } from "../constants/menuDefs";
import { getEnglishBetaGroupLabel, getEnglishBetaMenuLabel, isEnglishOperatorMode } from "../utils/operatorMode";

// --- SVG Icons ---
const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>;
const DeactivateIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>;
const ActivateIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14H6L5 6m3 0V4h8v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>;
const MailIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>;
const GoogleIcon = () => <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-4 h-4" />;

// --- Components ---
const USERS_COPY = {
    ja: {
        pageTitle: "👥 ユーザー管理",
        googleStatus: "Google連携",
        googleDone: "完了",
        googleMissing: "未連携",
        linkGoogle: "今すぐ連携する",
        addStaff: "新規スタッフ追加",
        inviteMode: "メールで招待 (推奨)",
        directMode: "パスワード直接登録",
        email: "メールアドレス",
        nameOptional: "氏名 (任意)",
        namePlaceholder: "山田 太郎",
        password: "ログインパスワード",
        passwordPlaceholder: "8文字以上",
        company: "会社名",
        companyPlaceholder: "株式会社〇〇",
        clientId: "クライアントID",
        role: "権限ロール",
        staff: "スタッフ",
        bookingManager: "日程調整担当",
        clientAdmin: "クライアント管理者",
        adminStaff: "管理者スタッフ",
        admin: "管理者",
        inviteHelp: "※ 指定したアドレスに招待メールが送られます。氏名は本人のGoogle情報から登録され、スタッフ権限は招待用の標準設定になります。個別権限を指定する場合はパスワード直接登録を使用してください。",
        directHelp: "※ パスワードを入力して即時にアカウントを作成します。Google連携は後から可能です。",
        oemHelp: "※ スタッフ用のログイン情報を入力して、アカウントを作成してください。",
        sendInvite: "招待メールを送信",
        createDirect: "直接登録する",
        searchPlaceholder: "メールアドレス、氏名、会社名で検索...",
        empty: "該当するユーザーがいません",
        userInfo: "ユーザー情報",
        lastLogin: "最終ログイン",
        connectionStatus: "連携 / 状態",
        permission: "権限",
        action: "操作",
        nameMissing: "(氏名未設定)",
        companyMissing: "会社名なし",
        noLogin: "未ログイン",
        stopped: "停止中",
        edit: "編集",
        deactivate: "無効化",
        activate: "有効化",
        delete: "削除",
        deleting: "削除中…",
        editTitle: "ユーザー編集",
        passwordChange: "パスワード変更",
        passwordChangePlaceholder: "変更しない場合は空欄のまま",
        passwordChangeHelp: "※ Google連携済みユーザーのパスワードを設定すると、パスワードでもログイン可能になります。",
        cancel: "キャンセル",
        saveUpdate: "更新を保存",
        permissionTitle: "アクセス権限設定",
        permissionHelp: "※ チェックを入れた機能のみ利用可能になります。",
        requiredEmail: "メールアドレスは必須です。",
        requiredEmailPassword: "メールアドレスとパスワードは必須です。",
        inviteSuccess: "招待メールを送信しました！",
        inviteFailure: "招待に失敗しました。",
        createSuccess: "登録が完了しました。",
        createFailurePrefix: "登録に失敗しました: ",
        createNetworkFailure: "通信エラーで登録できませんでした",
        updateSuccess: "ユーザー情報を更新しました。",
        updateFailurePrefix: "更新に失敗しました: ",
        confirmToggle: (actionText) => `このユーザーを本当に${actionText}しますか？\n(無効化するとGoogle連携も解除されます)`,
        confirmDelete: (email) => `${email} を完全に削除しますか？\nこの操作は取り消せません。`,
        deleteSuccess: "ユーザーを削除しました。",
        deleteFailurePrefix: "削除に失敗しました: ",
        operationFailurePrefix: "操作に失敗しました: ",
        paginationRange: (total, start, end) => `${total.toLocaleString()} 件中 ${start.toLocaleString()} - ${end.toLocaleString()} 件を表示`,
        previous: "前へ",
        next: "次へ"
    },
    en: {
        pageTitle: "User Management",
        googleStatus: "Google Connection",
        googleDone: "Connected",
        googleMissing: "Not Connected",
        linkGoogle: "Connect Google",
        addStaff: "Add Staff User",
        inviteMode: "Invite by Email",
        directMode: "Create with Password",
        email: "Email address",
        nameOptional: "Name (optional)",
        namePlaceholder: "Jane Smith",
        password: "Login password",
        passwordPlaceholder: "At least 8 characters",
        company: "Company",
        companyPlaceholder: "Company Inc.",
        clientId: "Client ID",
        role: "Role",
        staff: "Staff",
        bookingManager: "Booking Manager",
        clientAdmin: "Client Admin",
        adminStaff: "Admin Staff",
        admin: "System Admin",
        inviteHelp: "An invitation email will be sent to this address. The name comes from Google and invited staff receive the standard access set. Use password creation to choose individual permissions.",
        directHelp: "Create the account immediately with a password. Google can be connected later.",
        oemHelp: "Enter login information for this staff user and create the account.",
        sendInvite: "Send Invitation",
        createDirect: "Create User",
        searchPlaceholder: "Search by email, name, or company...",
        empty: "No matching users.",
        userInfo: "User",
        lastLogin: "Last Login",
        connectionStatus: "Connection / Status",
        permission: "Permissions",
        action: "Actions",
        nameMissing: "(Name not set)",
        companyMissing: "No company",
        noLogin: "Never logged in",
        stopped: "Suspended",
        edit: "Edit",
        deactivate: "Deactivate",
        activate: "Activate",
        delete: "Delete",
        deleting: "Deleting…",
        editTitle: "Edit User",
        passwordChange: "Change Password",
        passwordChangePlaceholder: "Leave blank to keep current password",
        passwordChangeHelp: "Setting a password lets Google-linked users also sign in with password login.",
        cancel: "Cancel",
        saveUpdate: "Save Changes",
        permissionTitle: "Access Permissions",
        permissionHelp: "Only checked features will be available to this user.",
        requiredEmail: "Email address is required.",
        requiredEmailPassword: "Email address and password are required.",
        inviteSuccess: "Invitation email sent.",
        inviteFailure: "Failed to send invitation.",
        createSuccess: "User created.",
        createFailurePrefix: "Failed to create user: ",
        createNetworkFailure: "Network error. Could not create user.",
        updateSuccess: "User updated.",
        updateFailurePrefix: "Failed to update user: ",
        confirmToggle: (actionText) => `Are you sure you want to ${actionText.toLowerCase()} this user?\nDeactivation also disconnects Google integration.`,
        confirmDelete: (email) => `Permanently delete ${email}?\nThis action cannot be undone.`,
        deleteSuccess: "User deleted.",
        deleteFailurePrefix: "Failed to delete user: ",
        operationFailurePrefix: "Operation failed: ",
        paginationRange: (total, start, end) => `Showing ${start.toLocaleString()} - ${end.toLocaleString()} of ${total.toLocaleString()} users`,
        previous: "Previous",
        next: "Next"
    }
};

const ENGLISH_PERMISSION_GROUP_LABELS = {
    dashboard: "Dashboard",
    preparation: "Setup",
    sending: "Execution",
    management: "Deals & CRM",
    marketing: "Marketing",
    account: "Account",
    api_keys: "API Keys"
};

const ENGLISH_PERMISSION_LABELS = {
    "/settings/booking": "Booking Settings",
    "/list-download": "List Download",
    "/client-sales-lists": "My Lists",
    "/orders": "Orders",
    "/sales-analytics": "Sales Analytics",
    "/marketing/leads": "Lead Management",
    "/marketing/email": "Email Campaigns",
    "/marketing/scenarios": "Scenario Campaigns",
    "/marketing/forms": "Forms",
    "/marketing/lps": "Landing Pages",
    "/marketing/presentations": "Documents",
    "/marketing/tracking": "Marketing Settings",
    "/marketing/meta-dashboard": "Meta Ads Analytics",
    "/users": "User Management",
    "/invoices": "Invoices",
    "/api-keys": "API Keys / AI Integration"
};

const getUsersCopy = (isEnglishBeta) => isEnglishBeta ? USERS_COPY.en : USERS_COPY.ja;

const getRoleLabel = (role, isEnglishBeta) => {
    const labels = isEnglishBeta
        ? { client_admin: "Admin", staff: "Staff", booking_manager: "Booking Manager", metatest: "Meta Review", admin_staff: "Admin Staff", admin: "System Admin" }
        : { client_admin: "管理者", staff: "スタッフ", booking_manager: "日程調整担当", metatest: "Meta審査用", admin_staff: "管理者スタッフ", admin: "システム管理者" };
    return labels[role] || role || "-";
};

const getPermissionGroupTitle = (group, isEnglishBeta) => {
    if (!isEnglishBeta) return group.title;
    return ENGLISH_PERMISSION_GROUP_LABELS[group.id] || getEnglishBetaGroupLabel(group, group.title);
};

const getPermissionItemLabel = (item, isEnglishBeta) => {
    if (!isEnglishBeta) return item.label;
    return ENGLISH_PERMISSION_LABELS[item.path] || getEnglishBetaMenuLabel(item.path, item.label);
};

function Pagination({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, currentItemCount, isEnglishBeta = false }) {
    const copy = getUsersCopy(isEnglishBeta);
    if (totalPages <= 1) return null;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = startItem + currentItemCount - 1;
    return (
        <div className="flex flex-col gap-3 mt-4 p-4 border-t sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-gray-700">
                {totalItems > 0 ? copy.paginationRange(totalItems, startItem, endItem) : copy.empty}
            </span>
            <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.previous}</button>
                <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.next}</button>
            </div>
        </div>
    );
}

const PermissionSelector = ({ permissions, onChange, user, isEnglishBeta = false }) => {
    const copy = getUsersCopy(isEnglishBeta);
    // 🚀 OEMクライアントに対して表示を制限するパスのリスト (menuDefs.js の制限と同期)
    const oemRestrictedPaths = ["/extension", "/account", "/invoices", "/help"];

    const handleCheckboxChange = (path, checked) => {
        if (checked) {
            onChange([...permissions, path]);
        } else {
            onChange(permissions.filter(p => p !== path));
        }
    };

    const handleGroupToggle = (groupItems, checked) => {
        const paths = groupItems.map(item => item.path);
        if (checked) {
            const newPermissions = [...new Set([...permissions, ...paths])];
            onChange(newPermissions);
        } else {
            const newPermissions = permissions.filter(p => !paths.includes(p));
            onChange(newPermissions);
        }
    };

    return (
        <div className="space-y-4 border p-4 rounded-md bg-gray-50 max-h-60 overflow-y-auto">
            <h4 className="font-bold text-gray-700 text-sm mb-2">{copy.permissionTitle}</h4>
            <div className="text-xs text-gray-500 mb-2">{copy.permissionHelp}</div>
            
            {MENU_GROUPS.map((group, idx) => {
                // --- 🚀 OEMユーザー（代理店）が操作している場合のフィルタリング処理 ---
                let currentGroup = { ...group };
                if (user?.oem_id) {
                    if (currentGroup.type === 'single') {
                        // シングルメニュー自体が制限対象なら表示しない
                        if (oemRestrictedPaths.includes(currentGroup.path)) return null;
                    } else {
                        // グループ内のアイテムから制限対象を除外
                        const visibleItems = currentGroup.items.filter(item => !oemRestrictedPaths.includes(item.path));
                        // 表示できるアイテムが1つもなくなったグループはまるごと非表示
                        if (visibleItems.length === 0) return null;
                        // フィルタリングしたアイテムリストを適用
                        currentGroup.items = visibleItems;
                    }
                }

                if (currentGroup.type === 'single') {
                    return (
                        <div key={idx} className="flex items-center mb-2">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={permissions.includes(currentGroup.path)}
                                    onChange={(e) => handleCheckboxChange(currentGroup.path, e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                />
                                <span className="text-sm font-medium text-gray-800">{getPermissionGroupTitle(currentGroup, isEnglishBeta)}</span>
                            </label>
                        </div>
                    );
                } else {
                    const allGroupPaths = currentGroup.items.map(i => i.path);
                    const isAllSelected = allGroupPaths.every(p => permissions.includes(p));
                    const isIndeterminate = allGroupPaths.some(p => permissions.includes(p)) && !isAllSelected;

                    return (
                        <div key={idx} className="mb-3 border-b border-gray-200 pb-2 last:border-0">
                            <div className="flex items-center mb-1">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        ref={el => el && (el.indeterminate = isIndeterminate)}
                                        checked={isAllSelected}
                                        onChange={(e) => handleGroupToggle(currentGroup.items, e.target.checked)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                    />
                                    <span className="text-sm font-bold text-gray-700">{getPermissionGroupTitle(currentGroup, isEnglishBeta)}</span>
                                </label>
                            </div>
                            <div className="ml-6 grid grid-cols-1 sm:grid-cols-2 gap-1">
                                {currentGroup.items.map((item, itemIdx) => (
                                    <label key={itemIdx} className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={permissions.includes(item.path)}
                                            onChange={(e) => handleCheckboxChange(item.path, e.target.checked)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2 h-3 w-3"
                                        />
                                        <span className="text-xs text-gray-600">{getPermissionItemLabel(item, isEnglishBeta)}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    );
                }
            })}
        </div>
    );
};

function UsersPage() {
  const { user } = useOutletContext(); 
  const isEnglishBeta = isEnglishOperatorMode();
  const copy = getUsersCopy(isEnglishBeta);
  const [users, setUsers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [form, setForm] = useState({ 
      email: "", name: "", role: "staff", company: "", password: "", client_id: "", 
      permissions: getAllPermissionPaths() 
  });
  
  const [regMode, setRegMode] = useState(user?.oem_id ? "direct" : "invite");
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingUserId, setDeletingUserId] = useState(null);
  
  // 🚀 修正: 固定ドメインを排除し、現在のオリジンを自動取得
  const API_URL = window.location.hostname === 'localhost' 
    ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
    : window.location.origin;
  const isOemAdminAccount = Boolean(user?.oem_id && ["admin", "admin_staff"].includes(user.role));
  const canCreateUsers = Boolean(
    user && ["admin", "admin_staff", "client_admin"].includes(user.role) && !isOemAdminAccount
  );

  // 🚀 修正: OEM社長(admin)やスタッフがプルダウンで選択したクライアントを反映
  const selectedClientId = useMemo(() => (
    user?.role === "admin" || user?.role === "admin_staff" 
      ? localStorage.getItem("selectedClientId") || "all" 
      : user?.client_id
  ), [user]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchUsers = useCallback(() => {
    if (!user) return;
    const params = new URLSearchParams({
        client_id: selectedClientId,
        page: currentPage,
        limit: 50,
        searchTerm: debouncedSearchTerm,
        role: user.role,
    });

    // ユーザー一覧の取得
    fetch(`${API_URL}/api/users?${params.toString()}`, { headers: getAuthHeader() })
      .then(res => res.json())
      .then(data => { 
          if (data.success) {
              setUsers(data.users || []);
              setPagination(data.pagination || null);
          }
      })
      .catch(err => console.error("ユーザー取得通信エラー:", err));
  }, [API_URL, selectedClientId, currentPage, debouncedSearchTerm, user]);

  useEffect(() => { 
    fetchUsers(); 
  }, [fetchUsers]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && pagination && newPage <= pagination.totalPages) {
        setCurrentPage(newPage);
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handlePermissionsChange = (newPermissions) => setForm({ ...form, permissions: newPermissions });
  const handleEditPermissionsChange = (newPermissions) => setEditingUser({ ...editingUser, permissions: newPermissions });

  // 招待メール送信
  const handleInvite = (e) => {
      e.preventDefault();
      if (!form.email) return alert(copy.requiredEmail);
      
      const payload = {
          email: form.email,
          role: form.role,
          permissions: form.role === 'staff' ? form.permissions : null,
          client_id: (user?.role === "client_admin" || user?.role === "staff") ? user.client_id : form.client_id
      };

      fetch(`${API_URL}/api/invite-staff`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          body: JSON.stringify(payload)
      })
      .then(res => res.json())
      .then(data => {
          if (data.success) {
              alert(copy.inviteSuccess);
              setForm({ ...form, email: "", name: "", password: "", permissions: getAllPermissionPaths() });
              fetchUsers();
          } else {
              alert(data.error || copy.inviteFailure);
          }
      });
  };

  // 直接登録（パスワード設定）
  const handleSubmitDirect = (e) => {
    e.preventDefault();
    let sendForm = { ...form };
    if (user?.role === "client_admin" || user?.role === "staff") {
    sendForm.client_id = user.client_id;
    sendForm.company = user.company;
    }
    if (!sendForm.email || !sendForm.password) return alert(copy.requiredEmailPassword);

    fetch(`${API_URL}/api/users`, {
      method: "POST", 
      headers: { "Content-Type": "application/json", ...getAuthHeader() }, 
      body: JSON.stringify(sendForm)
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          fetchUsers();
          setForm({ ...form, email: "", name: "", password: "", permissions: getAllPermissionPaths() });
          alert(copy.createSuccess);
        } else {
          alert(copy.createFailurePrefix + (data.error || ""));
        }
      })
      .catch(() => alert(copy.createNetworkFailure));
  };

  const startEdit = (userToEdit) => {
      let currentPermissions = [];
      try {
          if (Array.isArray(userToEdit.permissions)) {
              currentPermissions = userToEdit.permissions;
          } else if (typeof userToEdit.permissions === 'string') {
              currentPermissions = JSON.parse(userToEdit.permissions);
          } else {
               currentPermissions = getAllPermissionPaths();
          }
      } catch (e) {
          currentPermissions = getAllPermissionPaths();
      }
      setEditingUser({ ...userToEdit, password: "", permissions: currentPermissions });
  };

  const cancelEdit = () => setEditingUser(null);

  const handleUpdate = () => {
    if (!editingUser) return;
    const payload = {
      id: editingUser.id,
      role: editingUser.role,
      permissions: editingUser.role === 'staff' ? editingUser.permissions : null,
    };
    if (editingUser.password) payload.password = editingUser.password;

    fetch(`${API_URL}/api/users/update`, {
      method: "POST", 
      headers: { "Content-Type": "application/json", ...getAuthHeader() }, 
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          fetchUsers();
          setEditingUser(null);
          alert(copy.updateSuccess);
        } else {
          alert(copy.updateFailurePrefix + data.error);
        }
      });
  };
  
  const handleToggleActive = (id, currentIsActive) => {
      const actionText = currentIsActive ? copy.deactivate : copy.activate;
      if (!window.confirm(copy.confirmToggle(actionText))) return;

      fetch(`${API_URL}/api/users/toggle-active`, {
          method: 'POST', 
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          body: JSON.stringify({ id: id, is_active: !currentIsActive })
      })
      .then(res => res.json())
      .then(data => {
          if (data.success) fetchUsers();
          else alert(`${copy.operationFailurePrefix}${data.error}`);
      });
  };

  const canDeleteUser = useCallback((targetUser) => {
      if (!targetUser || !user || !["admin", "admin_staff", "client_admin"].includes(user.role)) return false;
      if (String(targetUser.id) === String(user.id)) return false;
      if (user.role === "client_admin") return targetUser.client_id === user.client_id;
      return true;
  }, [user]);

  const handleDelete = async (targetUser) => {
      if (!canDeleteUser(targetUser) || deletingUserId !== null) return;
      if (!window.confirm(copy.confirmDelete(targetUser.email))) return;

      setDeletingUserId(targetUser.id);
      try {
          const response = await fetch(`${API_URL}/api/users/${encodeURIComponent(targetUser.id)}`, {
              method: "DELETE",
              headers: getAuthHeader(),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data.success !== true) {
              throw new Error(data.error || `HTTP ${response.status}`);
          }
          setUsers((current) => current.filter((candidate) => String(candidate.id) !== String(targetUser.id)));
          setPagination((current) => current ? {
              ...current,
              totalItems: Math.max(0, Number(current.totalItems || 0) - 1),
          } : current);
          alert(copy.deleteSuccess);
          if (users.length === 1 && currentPage > 1) {
              setCurrentPage((page) => Math.max(1, page - 1));
          } else {
              fetchUsers();
          }
      } catch (error) {
          alert(`${copy.deleteFailurePrefix}${error?.message || ""}`);
      } finally {
          setDeletingUserId(null);
      }
  };

  const handleLinkGoogle = () => {
      window.location.href = `${API_URL}/api/auth/google`;
  };

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{copy.pageTitle}</h1>
            
            {/* 自分のGoogle連携状態セクション */}
            {!user?.oem_id && (
                <div className="mt-4 md:mt-0 bg-white border border-blue-100 rounded-lg p-3 flex flex-col gap-3 shadow-sm sm:flex-row sm:items-center sm:gap-4">
                    <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${user?.google_id ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className="text-sm font-medium text-gray-700">{copy.googleStatus}: {user?.google_id ? copy.googleDone : copy.googleMissing}</span>
                    </div>
                    {!user?.google_id && (
                        <button 
                            onClick={handleLinkGoogle}
                            className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
                        >
                            <GoogleIcon /> {copy.linkGoogle}
                        </button>
                    )}
                </div>
            )}
        </div>
        
        {/* 新規ユーザー登録/招待フォーム */}
        {canCreateUsers && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-8">
          <div className="flex flex-col justify-between gap-3 mb-6 border-b pb-2 sm:flex-row sm:items-center">
                <h2 className="text-xl font-semibold text-gray-700">{copy.addStaff}</h2>
                {/* 🏢 OEMユーザー以外の場合のみ切り替えトグルを表示 */}
                {!user?.oem_id && (
                    <div className="grid grid-cols-1 gap-1 bg-gray-100 p-1 rounded-lg sm:grid-cols-2">
                        <button 
                            onClick={() => {
                                setRegMode("invite");
                                setForm((current) => ({
                                    ...current,
                                    role: ["staff", "client_admin"].includes(current.role) ? current.role : "staff",
                                }));
                            }}
                            className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${regMode === "invite" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        >
                            {copy.inviteMode}
                        </button>
                        <button 
                            onClick={() => setRegMode("direct")}
                            className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${regMode === "direct" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        >
                            {copy.directMode}
                        </button>
                    </div>
                )}
            </div>

          <form onSubmit={regMode === "invite" ? handleInvite : handleSubmitDirect} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">{copy.email}</label>
                    <input type="email" name="email" placeholder="staff@example.com" required value={form.email} onChange={handleChange} className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500 w-full" />
                </div>
                {regMode === "direct" && <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">{copy.nameOptional}</label>
                    <input type="text" name="name" placeholder={copy.namePlaceholder} value={form.name} onChange={handleChange} className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500 w-full" />
                </div>}
                
                {regMode === "direct" && (
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">{copy.password}</label>
                        <input type="password" name="password" placeholder={copy.passwordPlaceholder} required={regMode === "direct"} value={form.password} onChange={handleChange} className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500 w-full" />
                    </div>
                )}

                {user?.role === "admin" && (
                <>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">{copy.company}</label>
                        <input type="text" name="company" placeholder={copy.companyPlaceholder} value={form.company} onChange={handleChange} className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500 w-full" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">{copy.clientId}</label>
                        <input type="text" name="client_id" placeholder="client_xxx" value={form.client_id} onChange={handleChange} className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500 w-full" />
                    </div>
                </>
                )}
                
                <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">{copy.role}</label>
                    <select name="role" value={form.role} onChange={handleChange} className="p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500 w-full">
                        <option value="staff">{copy.staff}</option>
                        {regMode === "direct" && <option value="booking_manager">{copy.bookingManager}</option>}
                        <option value="client_admin">{copy.clientAdmin}</option>
                        {regMode === "direct" && user?.role === "admin" && (
                            <>
                            <option value="admin_staff">{copy.adminStaff}</option>
                            <option value="admin">{copy.admin}</option>
                            </>
                        )}
                    </select>
                </div>
            </div>
            
            {regMode === 'direct' && form.role === 'staff' && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                    <PermissionSelector permissions={form.permissions} onChange={handlePermissionsChange} user={user} isEnglishBeta={isEnglishBeta} />
                </div>
            )}

            <div className="flex flex-col gap-3 bg-blue-50 p-4 rounded-lg sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-blue-700">
                    {user?.oem_id ? (
                        // 🚀 OEM時はGoogleの文字を出しにい。パスワード登録のみなので説明もシンプルに。
                        copy.oemHelp
                    ) : (
                        // 本家用の出し分け
                        regMode === "invite" 
                            ? copy.inviteHelp
                            : copy.directHelp
                    )}
                </p>
                <button type="submit" className={`w-full font-bold py-2.5 px-8 rounded-lg shadow-md transition-all transform hover:scale-105 sm:w-auto ${regMode === "invite" ? "bg-purple-600 text-white hover:bg-purple-700" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
                    {regMode === "invite" ? copy.sendInvite : copy.createDirect}
                </button>
            </div>
          </form>
        </div>
        )}

        {/* ユーザー一覧 */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white">
            <div className="relative flex-1">
                <input 
                  type="text"
                  placeholder={copy.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <div className="divide-y divide-gray-100 md:hidden">
              {users.length > 0 ? users.map(u => (
                <div key={`${u.id}-mobile`} className={`p-4 ${u.is_active === 0 ? 'bg-red-50 opacity-80' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 break-all">{u.email}</p>
                      <p className="mt-1 text-xs text-gray-500">{u.name || copy.nameMissing}</p>
                      <p className="mt-1 text-xs text-gray-500 break-words">{u.company || copy.companyMissing}</p>
                    </div>
                    <span className={`flex-shrink-0 px-2 py-1 rounded text-xs font-bold ${u.role === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-blue-50 text-blue-600'}`}>
                      {getRoleLabel(u.role, isEnglishBeta)}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {!user?.oem_id && (
                      u.google_id ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 font-bold text-green-700"><GoogleIcon /> LINKED</span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-1 font-bold text-gray-500">PASSWORD</span>
                      )
                    )}
                    {!u.is_active && <span className="rounded-full bg-red-100 px-2 py-1 font-bold text-red-700">{copy.stopped}</span>}
                  </div>
                  <p className="mt-3 text-xs text-gray-500">{copy.lastLogin}: {u.last_login_at ? new Date(u.last_login_at).toLocaleString(isEnglishBeta ? 'en-US' : 'ja-JP') : copy.noLogin}</p>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button onClick={() => startEdit(u)} className="rounded-md bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100">{copy.edit}</button>
                    {u.is_active ? (
                      <button onClick={() => handleToggleActive(u.id, u.is_active)} className="rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-100">{copy.deactivate}</button>
                    ) : (
                      <button onClick={() => handleToggleActive(u.id, u.is_active)} className="rounded-md bg-green-50 px-3 py-2 text-sm font-semibold text-green-700 hover:bg-green-100">{copy.activate}</button>
                    )}
                    {canDeleteUser(u) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(u)}
                        disabled={deletingUserId !== null}
                        className="col-span-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`${u.email}を${copy.delete}`}
                      >
                        {deletingUserId === u.id ? copy.deleting : copy.delete}
                      </button>
                    )}
                  </div>
                </div>
              )) : (
                <div className="px-4 py-10 text-center text-sm text-gray-500">{copy.empty}</div>
              )}
            </div>
            <table className="hidden w-full text-sm text-left text-gray-600 md:table">
              <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="py-4 px-6">{copy.userInfo}</th>
                  <th className="py-4 px-6">{copy.company}</th>
                  <th className="py-4 px-6">{copy.lastLogin}</th>
                  <th className="py-4 px-6 text-center">{copy.connectionStatus}</th>
                  <th className="py-4 px-6">{copy.permission}</th>
                  <th className="py-4 px-6 text-right">{copy.action}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map(u => (
                  <tr key={u.id} className={`hover:bg-gray-50 transition-colors ${u.is_active === 0 ? 'bg-red-50 opacity-60' : ''}`}>
                    <td className="py-4 px-6 font-medium">
                        <div className="text-gray-900">{u.email}</div>
                        <div className="text-xs text-gray-500">{u.name || copy.nameMissing}</div>
                    </td>
                    <td className="py-4 px-6">{u.company || copy.companyMissing}</td>
                    <td className="py-4 px-6">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString(isEnglishBeta ? 'en-US' : 'ja-JP') : copy.noLogin}
                    </td>
                    <td className="py-4 px-6 text-center">
                        <div className="flex flex-col items-center gap-1">
                            {/* 🚀 修正：ログインユーザーが OEM の場合は Google 関連のタグを隠します */}
                            {!user?.oem_id ? (
                                <div className="flex items-center gap-1.5">
                                    {u.google_id ? (
                                        <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 font-bold">
                                            <GoogleIcon /> LINKED
                                        </span>
                                    ) : (
                                        <span className="bg-gray-100 text-gray-500 text-[10px] px-2 py-0.5 rounded-full font-bold">
                                            PASSWORD
                                        </span>
                                    )}
                                </div>
                            ) : (
                                /* 🚀 OEM時は代わりにシンプルな「有効」バッジを表示（任意） */
                                u.is_active ? (
                                    <span className="bg-blue-100 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                                        ACTIVE
                                    </span>
                                ) : null
                            )}
                            {!u.is_active && <span className="text-[10px] text-red-600 font-bold">{copy.stopped}</span>}
                        </div>
                    </td>
                    <td className="py-4 px-6">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${u.role === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-blue-50 text-blue-600'}`}>
                            {getRoleLabel(u.role, isEnglishBeta)}
                        </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => startEdit(u)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title={copy.edit}><EditIcon /></button>
                        {u.is_active ? (
                            <button onClick={() => handleToggleActive(u.id, u.is_active)} className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors" title={copy.deactivate}><DeactivateIcon /></button>
                        ) : (
                            <button onClick={() => handleToggleActive(u.id, u.is_active)} className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors" title={copy.activate}><ActivateIcon /></button>
                        )}
                        {canDeleteUser(u) && (
                            <button
                              type="button"
                              onClick={() => handleDelete(u)}
                              disabled={deletingUserId !== null}
                              className="p-2 text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                              title={copy.delete}
                              aria-label={`${u.email}を${copy.delete}`}
                            >
                              <DeleteIcon />
                            </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {pagination && <Pagination 
              currentPage={pagination.currentPage} 
              totalPages={pagination.totalPages} 
              onPageChange={handlePageChange}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              currentItemCount={users.length}
              isEnglishBeta={isEnglishBeta}
          />}
        </div>

        {/* 編集モーダル */}
        {editingUser && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-stretch justify-center z-50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
                <div className="bg-white rounded-none shadow-2xl w-full max-w-2xl max-h-none flex flex-col animate-in fade-in zoom-in duration-200 sm:max-h-[90vh] sm:rounded-xl">
                    <div className="p-4 border-b flex justify-between items-center bg-gray-50 sm:rounded-t-xl sm:p-5">
                        <h3 className="text-xl font-bold text-gray-800">{copy.editTitle}: {editingUser.name || editingUser.email}</h3>
                        <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                    </div>
                    <div className="p-4 overflow-y-auto bg-white sm:p-6">
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">{copy.role}</label>
                                <select 
                                    value={editingUser.role} 
                                    onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                                    className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="staff">{copy.staff}</option>
                                    <option value="booking_manager">{copy.bookingManager}</option>
                                    <option value="client_admin">{copy.clientAdmin}</option>
                                    {user?.role === "admin" && (
                                        <>
                                        <option value="admin_staff">{copy.adminStaff}</option>
                                        <option value="admin">{copy.admin}</option>
                                        </>
                                    )}
                                </select>
                            </div>
                            
                            {editingUser.role === 'staff' && (
                                <div className="border rounded-lg p-4 bg-blue-50/30">
                                    <PermissionSelector permissions={editingUser.permissions || []} onChange={handleEditPermissionsChange} user={user} isEnglishBeta={isEnglishBeta} />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">{copy.passwordChange}</label>
                                <input 
                                    type="password" 
                                    value={editingUser.password} 
                                    onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                                    placeholder={copy.passwordChangePlaceholder}
                                    className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500" 
                                />
                                <p className="text-[10px] text-gray-500 mt-1">{copy.passwordChangeHelp}</p>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 border-t bg-gray-50 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:rounded-b-xl sm:p-5">
                        <button onClick={cancelEdit} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-600 font-bold hover:bg-gray-200 transition-colors">{copy.cancel}</button>
                        <button onClick={handleUpdate} className="px-8 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-md transition-all">{copy.saveUpdate}</button>
                    </div>
                </div>
            </div>
        )}

      </div>
    </div>
  );
}

export default UsersPage;
